import { Promise } from 'bluebird';
import _ from 'underscore';
import { parseSync } from '@bingads-webui/safe-json';

export const fetchImgPromise = imgUrl => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => {
    resolve(image);
  };
  image.onerror = () => {
    reject(new Error('Failed to load image'));
  };
  image.crossOrigin = '';
  image.src = imgUrl;
});

export const createOffScreenContext = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  return {
    canvas,
    context,
  };
};

export const getImagePixels = (image, imageWidth, imageHeight) => {
  const {
    canvas,
    context,
  } = createOffScreenContext(imageWidth, imageHeight);

  context.drawImage(image, 0, 0);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  return pixels;
};

export const parseMaskResultPromise = ({
  result,
  imageUrl,
  imageWidth,
  imageHeight,
}) => new Promise((resolve, reject) => {
  const { rawMaskSetsBase64String } = result;
  const maskSetsBase64Json = parseSync(rawMaskSetsBase64String);
  if (_.isUndefined(maskSetsBase64Json)) {
    reject(new Error('Failed to parse result'));
  }
  const {
    shape = [],
    data: maskSetsBase64,
  } = maskSetsBase64Json;

  // Convert the decompressed data into a 3D array
  const reshapeTo3D = (array, x, y, z) => {
    const res = [];
    let index = 0;
    for (let i = 0; i < x; i += 1) {
      const temp2D = [];
      for (let j = 0; j < y; j += 1) {
        const temp1D = [];
        for (let k = 0; k < z; k += 1) {
          temp1D.push(array[index]);
          index += 1;
        }
        temp2D.push(temp1D);
      }
      res.push(temp2D);
    }
    return res;
  };

  async function decompressAndParse3DArray(base64String, x, y, z) {
    // Step 1: Decode the Base64 string to a binary Uint8Array
    const binaryString = atob(base64String);
    const byteArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
      byteArray[i] = binaryString.charCodeAt(i);
    }

    // Step 2: Create a readable stream from the byteArray
    const inputStream = new Blob([byteArray]).stream();

    // Step 3: Decompress the stream using DecompressionStream (assuming 'gzip')
    const decompressionStream = new DecompressionStream('gzip');
    const decompressedStream = inputStream.pipeThrough(decompressionStream);

    // Step 4: Collect the decompressed data
    const decompressedResponse = new Response(decompressedStream);
    const decompressedBuffer = await decompressedResponse.arrayBuffer();

    // Step 5: Convert the decompressed data to a Uint8Array
    const decompressedArray = new Uint8Array(decompressedBuffer);

    // Step 6: Reshape the decompressed array into the specified 3D dimensions (x, y, z)
    const int3DArray = reshapeTo3D(decompressedArray, x, y, z);

    return int3DArray;
  }

  // Execute the function and log the result
  decompressAndParse3DArray(maskSetsBase64, shape[1], shape[0], shape[2])
    .then((maskSets3Darray) => {
      const maskSetsArray = maskSets3Darray.map(row => row.map(a => a[0])).flat();
      const alphaArray = maskSets3Darray.map(row => row.map(a => a[1])).flat();
      const selectedMasksArray = _.filter(_.uniq(maskSetsArray), pixelMask => pixelMask > 0);
      fetchImgPromise(imageUrl).then((image) => {
        const {
          canvas,
          context,
        } = createOffScreenContext(imageWidth, imageHeight);

        context.drawImage(image, 0, 0);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        return pixels;
      }).then((originalImagePixels) => {
        if (originalImagePixels && maskSetsArray) {
          // If not in mask, return empty and transparent pixel
          // If in mask, return the original pixel
          const {
            canvas,
            context,
          } = createOffScreenContext(imageWidth, imageHeight);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          for (let i = 0; i < pixels.length; i += 4) {
            if (maskSetsArray[i / 4] > 0) {
              pixels[i] = originalImagePixels[i];
              pixels[i + 1] = originalImagePixels[i + 1];
              pixels[i + 2] = originalImagePixels[i + 2];
              pixels[i + 3] = alphaArray[i / 4];
            }
          }
          // put the selected image from first canvas to second canvas
          const { context: secondContext, canvas: secondCanvas } = createOffScreenContext(imageWidth, imageHeight);
          secondContext.putImageData(imageData, 0, 0);
          resolve({
            rawMaskSetsBase64String,
            maskSetsArray,
            alphaArray,
            selectedMasksArray,
            imageUrlWithoutBackground: secondCanvas.toDataURL(),
          });
        } else {
          reject(new Error('Failed to parse result'));
        }
      });
    })
    .catch(() => {
      reject(new Error('Failed to parse result'));
    });
});

export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
}

export function getMaskValueAtPointFunc(x, y, imageWidth, segmentationMasks) {
  if (x < 0 || y < 0) {
    return null;
  }
  // Should transform the point to the original point on the image size
  const width = imageWidth;

  // get index of the mask array
  const index = (parseInt(y, 10) * width) + parseInt(x, 10);
  // get mask value at the index
  return segmentationMasks[index];
}

export function createColorBackground(image, imageWidth, imageHeight, color, segmentationMasks, selectedMasks, alphaArray) {
  // If not in mask, return empty and transparent pixel
  // If in mask, return the original pixel
  const { r, g, b } = hexToRgb(color);
  const {
    canvas,
    context,
  } = createOffScreenContext(imageWidth, imageHeight);

  context.drawImage(image, 0, 0);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  for (let i = 0; i < pixels.length; i += 4) {
    if (!selectedMasks.includes(segmentationMasks[i / 4])) {
      pixels[i] = r;
      pixels[i + 1] = g;
      pixels[i + 2] = b;
      pixels[i + 3] = 255;
    } else {
      const alpha = alphaArray[i / 4] / 255;
      const newR = ((1 - alpha) * r) + ((alpha) * pixels[i]);
      const newG = ((1 - alpha) * g) + ((alpha) * pixels[i + 1]);
      const newB = ((1 - alpha) * b) + ((alpha) * pixels[i + 2]);
      pixels[i] = newR;
      pixels[i + 1] = newG;
      pixels[i + 2] = newB;
      pixels[i + 3] = 255;
    }
  }
  // put the selected image from first canvas to second canvas
  const { context: secondContext, canvas: secondCanvas } = createOffScreenContext(imageWidth, imageHeight);
  secondContext.putImageData(imageData, 0, 0);
  return secondCanvas.toDataURL();
}

export function createSelectedImageSrc(image, imageWidth, imageHeight, originalImagePixels, segmentationMasks, masks, isAdd, hasSelectedImageSrc) {
  // If not in mask, return empty and transparent pixel
  // If in mask, return the original pixel
  const {
    canvas,
    context,
  } = createOffScreenContext(imageWidth, imageHeight);

  if (hasSelectedImageSrc) {
    context.drawImage(image, 0, 0);
  }

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;

  for (let i = 0; i < pixels.length; i += 4) {
    if (masks.includes(segmentationMasks[i / 4])) {
      if (isAdd) {
        pixels[i] = originalImagePixels[i];
        pixels[i + 1] = originalImagePixels[i + 1];
        pixels[i + 2] = originalImagePixels[i + 2];
        pixels[i + 3] = originalImagePixels[i + 3];
      } else {
        pixels[i] = 0;
        pixels[i + 1] = 0;
        pixels[i + 2] = 0;
        pixels[i + 3] = 0;
      }
    }
  }

  const { context: secondContext, canvas: secondCanvas } = createOffScreenContext(imageWidth, imageHeight);
  secondContext.putImageData(imageData, 0, 0);

  const dArr = [-1, -1, 0, -1, 1, -1, -1, 0, 1, 0, -1, 1, 0, 1, 1, 1]; // offset array
  const s = 6; // thickness scale

  context.clearRect(0, 0, canvas.width, canvas.height);
  // draw images at offsets from the array scaled by s
  for (let i = 0; i < dArr.length; i += 2) {
    context.drawImage(secondCanvas, dArr[i] * s, dArr[i + 1] * s);
  }

  // fill with color
  context.globalCompositeOperation = 'source-in';
  context.fillStyle = '#D1ACFF';
  context.fillRect(0, 0, canvas.width, canvas.height);

  return ({
    borderImageSrc: canvas.toDataURL(),
    selectedImageSrc: secondCanvas.toDataURL(),
  });
}

export function createHighlightedImage(imageWidth, imageHeight, originalImagePixels, segmentationMasks, workingMasks, maskValue) {
  const {
    canvas,
    context,
  } = createOffScreenContext(imageWidth, imageHeight);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;

  if (originalImagePixels) {
    for (let i = 0; i < pixels.length; i += 4) {
      if (segmentationMasks[i / 4] === maskValue) {
        if (workingMasks.includes(maskValue)) {
          pixels[i] = (originalImagePixels[i] + 1672) / 9; // X + Y * 8) / 9
          pixels[i + 1] = (originalImagePixels[i + 1] + 1376) / 9;
          pixels[i + 2] = (originalImagePixels[i + 2] + 2040) / 9;
          pixels[i + 3] = originalImagePixels[i + 3];
        } else if (maskValue > 0) {
          pixels[i] = originalImagePixels[i];
          pixels[i + 1] = originalImagePixels[i + 1];
          pixels[i + 2] = originalImagePixels[i + 2];
          pixels[i + 3] = 128;
        }
      }
    }
  }

  context.putImageData(imageData, 0, 0);

  return canvas.toDataURL();
}
